/* ============================================================
   Home — ink hero, audience doors, then the tools.

   The previous version opened with a logo, a paragraph, and immediately
   a single column of thirteen identical cards. That reads as a utility
   app. A page earns a premium read from rhythm: a dark hero that states
   the claim, a split that says who this is for, a stat band that proves
   it, and only then the working surface.
   ============================================================ */

import { getBrand } from '../shared/brand.js';
import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { TRACKS, toolsInTrack, liveTools } from '../data/tools.js';
import { REGULATIONS, statusOf, STATUS_META } from '../data/regulations.js';
import { BENCHMARKS } from '../data/benchmarks.js';
import { shortDate, daysUntil, relativeDays } from '../shared/format.js';
import { ToolCard } from '../components/ToolCard.js';
import { Logo } from '../components/Logo.js';
import { router } from '../shared/router.js';

const TRUST = [
  { icon: 'lock', label: 'Nothing leaves your device' },
  { icon: 'check', label: 'Every figure sourced and dated' },
  { icon: 'globe', label: 'Works offline, installs like an app' },
  /* "from every tool" was not true. Sixteen of the eighteen export;
     the glossary is a reference and the saved list is an index, and
     neither computes anything to put in front of a board. Adding a
     PDF to those two would have made the old sentence true and meant
     less. check-data enforces the sentence as written. */
  { icon: 'document', label: 'Board-ready PDF from every tool that computes a result' }
];

export function HomePage() {
  const brand = getBrand();
  const page = document.createElement('div');
  page.className = 'animate-fade-in';

  const inForce = REGULATIONS.filter((r) => statusOf(r) === 'in-force').length;
  const corridors = Object.keys(BENCHMARKS).length;
  const tools = liveTools().length;

  page.innerHTML = html`
    <!-- ---------- Hero ---------- -->
    <section class="hero">
      <div class="container">
        <div class="hero-grid">
          <div>
            <div class="hero-lockup" id="hero-logo"></div>
            <span class="eyebrow">Corridor logistics intelligence · Kenya &amp; the EAC</span>
            <h1>Know what the corridor <em>actually</em> costs you.</h1>
            <p class="lede">
              ${brand.tagline}. Cost per tonne-kilometre, landed cost, demurrage exposure and
              every regulatory deadline that applies to you — computed here, in this browser,
              and never sent anywhere.
            </p>
            <div class="hero-actions">
              <button class="btn btn-on-ink btn-lg" id="cta-scorecard" type="button">
                Run the diagnostic
              </button>
              <button class="btn btn-ghost-ink btn-lg" id="cta-benchmark" type="button">
                Cost a corridor
              </button>
            </div>
            <div class="trust-strip">
              ${TRUST.map(
                (t) => html`
                  <span class="trust-item">${raw(icon(t.icon, 15))} ${t.label}</span>
                `
              )}
            </div>
          </div>
          <aside class="hero-panel" id="hero-panel"></aside>
        </div>
      </div>
    </section>

    <!-- ---------- Stat band ---------- -->
    <section class="band band-tight band-ink" style="background: var(--kanda-indigo);">
      <div class="container">
        <div class="stat-strip">
          <div>
            <div class="stat-value"><em>${tools}</em></div>
            <div class="stat-label">tools, all free, no account</div>
          </div>
          <div>
            <div class="stat-value">${inForce}</div>
            <div class="stat-label">instruments in force today, status computed not stored</div>
          </div>
          <div>
            <div class="stat-value">${corridors}</div>
            <div class="stat-label">corridors benchmarked, every band sourced</div>
          </div>
          <div>
            <div class="stat-value">0</div>
            <div class="stat-label">bytes of your data transmitted</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ---------- The two doors ---------- -->
    <section class="band" style="padding-bottom: 0;">
      <div class="container">
        <span class="eyebrow">Two tracks</span>
        <h2 style="font-size: var(--ca-text-xl); margin-bottom: var(--ca-space-6); max-width: 20ch;">
          Which side of the problem are you on?
        </h2>
        <div class="doors" id="doors"></div>
      </div>
    </section>

    <!-- ---------- Tools ---------- -->
    <div id="tracks"></div>

    <!-- ---------- Privacy ---------- -->
    <section class="band band-tight band-cream">
      <div class="container">
        <div style="max-width: 62ch;">
          <span class="eyebrow">${raw(icon('lock', 13))} Privacy by architecture</span>
          <h2 style="font-size: var(--ca-text-lg); margin-bottom: var(--ca-space-3);">
            We cannot see your numbers, because there is nowhere for them to go.
          </h2>
          <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary);">
            No accounts, no tracking, no analytics. Every calculation runs in this browser and
            every diagnostic is stored locally. The app makes no network requests after first
            load — enforced by a content security policy, not merely promised. The honest
            corollary: we cannot recover anything for you either, so export what matters.
          </p>
          <div class="row" style="gap: var(--ca-space-3); margin-top: var(--ca-space-5); flex-wrap: wrap;">
            <button class="btn btn-secondary" id="cta-privacy" type="button">Privacy notice</button>
            <button class="btn btn-ghost" id="cta-about" type="button">Method &amp; sources</button>
          </div>
        </div>
      </div>
    </section>
  `;

  /* ---- Hero lockup ---- */
  const hero = Logo({
    height: 30,
    name: brand.shortName,
    descriptor: brand.descriptor,
    showDescriptor: Boolean(brand.descriptor)
  });
  hero.classList.add('logo-hero');
  hero.style.setProperty('--brand-primary', 'var(--kanda-sand)');
  hero.querySelector('.logo-name').style.color = 'var(--kanda-sand)';
  page.querySelector('#hero-logo').appendChild(hero);

  /* ---- Live regulatory panel ---- */
  page.querySelector('#hero-panel').appendChild(buildPanel());

  /* ---- Doors ---- */
  const doors = page.querySelector('#doors');
  for (const track of Object.values(TRACKS)) {
    const count = toolsInTrack(track.id).filter((t) => t.status === 'live').length;
    const door = document.createElement('button');
    door.type = 'button';
    door.className = 'door';
    door.innerHTML = html`
      <span class="eyebrow">${track.label}</span>
      <h2>${track.caption}</h2>
      <p>${track.blurb}</p>
      <span class="door-meta">
        <span class="num">${count}</span> tools ${raw(icon('right', 15))}
      </span>
    `;
    door.addEventListener('click', () => {
      const target = page.querySelector(`#track-${track.id}`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    doors.appendChild(door);
  }

  /* ---- Tool sections ---- */
  const tracks = page.querySelector('#tracks');
  Object.values(TRACKS).forEach((track, i) => {
    const section = document.createElement('section');
    // The doors band above already opened the movement, so the first
    // track continues it rather than starting a second one.
    section.className = `band${i % 2 === 1 ? ' band-cream' : ' band-tight'}`;
    section.id = `track-${track.id}`;
    section.innerHTML = html`
      <div class="container">
        <span class="eyebrow">${track.label}</span>
        <h2 style="font-size: var(--ca-text-xl); margin-bottom: var(--ca-space-2);">
          ${track.caption}
        </h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); max-width: 54ch; margin-bottom: var(--ca-space-6);">
          ${track.blurb}
        </p>
        <div class="tool-grid" data-grid></div>
      </div>
    `;
    const grid = section.querySelector('[data-grid]');
    for (const tool of toolsInTrack(track.id)) grid.appendChild(ToolCard(tool));
    tracks.appendChild(section);
  });

  /* ---- Actions ---- */
  const go = (id, path) =>
    page.querySelector(id).addEventListener('click', () => router.navigate(path));
  go('#cta-scorecard', '/scorecard');
  go('#cta-benchmark', '/benchmark');
  go('#cta-privacy', '/privacy');
  go('#cta-about', '/about');

  return page;
}

/**
 * The hero's right-hand panel: what the regulatory clock is doing right
 * now. Static marketing copy would be cheaper, but a live figure is the
 * thing that makes the product feel current rather than brochure-like.
 */
function buildPanel() {
  const now = new Date();

  const dated = REGULATIONS.map((r) => ({
    ...r,
    status: statusOf(r, now),
    days: daysUntil(r.effectiveDate, now)
  }));

  // Whatever is landing next, then whatever landed most recently.
  const upcoming = dated
    .filter((r) => r.days > 0)
    .sort((a, b) => a.days - b.days);
  const recent = dated.filter((r) => r.days <= 0).sort((a, b) => b.days - a.days);
  const rows = [...upcoming.slice(0, 2), ...recent.slice(0, 3 - Math.min(2, upcoming.length))];

  const panel = document.createElement('div');
  panel.innerHTML = html`
    <!-- h2, not h3: this panel sits directly under the page h1 and there
         is no h2 between them. Skipping a level leaves a screen-reader
         user navigating by heading unable to tell whether they have
         missed a section. -->
    <h2>The regulatory clock</h2>
    ${rows.map(
      (r) => html`
        <div class="panel-row">
          <span style="flex-shrink:0; margin-top:2px; color: ${
            r.status === 'imminent' ? 'var(--kanda-amber-light)' : 'var(--kanda-mint)'
          };">
            ${raw(icon(r.status === 'imminent' ? 'alert' : 'check', 14))}
          </span>
          <span style="flex:1; min-width:0;">
            <span class="panel-name">${r.name}</span>
            <span class="panel-meta">
              ${STATUS_META[r.status].label} · ${shortDate(r.effectiveDate)} ·
              ${relativeDays(r.days)}
            </span>
          </span>
        </div>
      `
    )}
  `;

  const link = document.createElement('button');
  link.type = 'button';
  link.className = 'btn btn-ghost-ink btn-sm';
  link.style.marginTop = 'var(--ca-space-4)';
  link.style.width = '100%';
  link.textContent = 'Open the compliance calendar';
  link.addEventListener('click', () => router.navigate('/compliance'));
  panel.appendChild(link);

  return panel;
}
